import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import Image from 'next/image';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import BestSellerIcon from '../../../../public/assets/img/bestseller-icon@1x.png';
import view_in_your_room_icon from '../../assets/images/view_in_your_room_icon.png';
import cssStyles from "./Product.module.scss";
import AppleDownloadPopupLayerButton from "./AppleDownloadPopupLayerButton";
import ProductOptions from './ProductOptions'
import FavoriteButton from './FavoriteButton'
export interface ProductType {
  id?: string,
  name?: string,
  productType?: string,
  productTypeDisplayName?: string,
  productSubType?: string,
  productSubTypeDisplayName?: string,
  image?: string,
  model3dUrl?: string,
  price?: number,
  comparePrice?: number,
  pdpUrl?: string,
  viewMoreUrl?: string,
  vendorName?: string,
  expiresOn?: string,
  viewInYourRoomLink?: string,
  otherSizes?: any,
  otherColors?: any,
  otherFabrics?: any,
  configurationDisplayName?: any,
  configurationIcon?: any,
  priceRange?: any,
  colorVariants?: any,
  isBestSeller?: boolean
}

// CardData type should be imported or defined as per your codebase
interface ProductProps {
  card: CarouselCardData;
  favorites?: any;
  setFavorites?: any;
  customerId?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>, card: CarouselCardData) => void;
  height?: string;
  width?:string;
  pageName?: string;
}

export interface CarouselCardData {
    id?: string;
    name?: string;
    imageUrl?: string;
    image?: string | string[];
    imageHeight?: string;
    description?: string;
    link?: string;
    pdpUrl?: string;
    excludeBaseURI?: boolean;
    viewMoreLink?: string;
    height?: string;
    width?: string;
    className?: string;
    index? :number;
    price?: number;
    vendorName?: string;
    comparePrice?: number;
    expiresOn?: string;
    model3dUrl?: string;
    viewInYourRoomLink?: string;
    otherSizes?: any;
    otherFabrics?: any;
    otherColors?: any;
    colorVariants?: any;
    configurationDisplayName?: any;
    configurationIcon?: any;
    priceRange?: any;
    lazyLoad?: boolean;
    isBestSeller?: boolean;
    trackObj? : {
        clickId: string;
        event: string;
        name?: string;
        value?: string;
        valuePropName?: string;
    }
    callback?: (arg0: CarouselCardData) => void;
}

export const prepareCardsForBuilderProductsGridLocal = (data: ProductType[], brandPathEntry?: string, vendorsData?: any): CarouselCardData[] => {
    const showViewMore = false;
    const productsCards = data?.map((eachSlide: ProductType) => {
        let vendorName = vendorsData?.vendors?.find((vendorObj: any) => vendorObj.vendorName === eachSlide.vendorName)?.vendorDisplayName;
        return {
            id: eachSlide.id,
            name: eachSlide.name,
            imageUrl: eachSlide.image,
            link: eachSlide.pdpUrl,
            viewMoreLink: showViewMore ? eachSlide.viewMoreUrl : null,
            imageHeight: "18rem",
            height: "25rem",
            width: "24rem",
            price: eachSlide.price,
            model3dUrl: eachSlide.model3dUrl,
            comparePrice: eachSlide.comparePrice,
            expiresOn: eachSlide.expiresOn,
            viewInYourRoomLink: eachSlide.viewInYourRoomLink,
            vendorName,
            priceRange: eachSlide.priceRange,
            otherColors: eachSlide.otherColors,
            otherSizes: eachSlide.otherSizes,
            otherFabrics: eachSlide.otherFabrics,
            configurationDisplayName: eachSlide.configurationDisplayName,
            configurationIcon: eachSlide.configurationIcon,
            colorVariants: eachSlide.colorVariants,
            isBestSeller: eachSlide.isBestSeller
        } as CarouselCardData;
    });
    return productsCards;
};

const Product: React.FC<ProductProps> = ({ card, favorites, setFavorites, customerId, onClick, height, width, pageName }) => {
  const [hoverOnColor, setHoverOnColor] = useState<{ [key: string | number]: string }>({});
  const handleHover = (productId: number, image: string) => {
    setHoverOnColor((prev) => ({
      ...prev,
      [productId]: image,
    }));
  };

  const handleHoverOut = (productId: number) => {
    setHoverOnColor({});
  };
  // Ensure imageUrl is a string for next/image
  const imageUrl = (card?.id && hoverOnColor?.[card.id]) ? hoverOnColor[card.id] : (Array.isArray(card.image) ? card.image[0] : card.image || "");
  // const imageUrl = Array.isArray(card.image) ? card.image[0] : card.image || "";
  const customStyle: React.CSSProperties = height && width ? { width: parseInt(width) } : {};
  return (
    <Box component='div' className={cssStyles["card"]} onClick={(e: React.MouseEvent<HTMLDivElement>) => onClick && onClick(e, card)} style={customStyle}>
      <div className={cssStyles["image-wrapper"]}>
        {card.id && <FavoriteButton customerId={customerId} productId={card.id} productName={card.name} price={card.price} comparePrice={card.comparePrice} imageUrl={card.image} model3dUrl={card.model3dUrl} favorites={favorites} setFavorites={setFavorites} />}
        <a id={`${pageName}_product_${card.id}`} href={card?.pdpUrl ?? "#"}>
          <Image
            src={imageUrl}
            alt={card.name || ""}
            width={parseInt(height as string ?? "200")}
            height={parseInt(height as string ?? "200")}
            objectFit="contain"
            style={{
              objectFit: "contain",
              borderWidth: "1px",
              borderStyle: "solid",
              borderRadius: '10px',
              marginBottom: '10px',
              borderColor: "lightgray",
            }}
          />
        </a>
        {card.isBestSeller && (
          <div className={cssStyles["bestSeller-icon"]}>
            <img src={BestSellerIcon.src} alt="Best Seller" />
          </div>
        )}
      </div>
      {/* product colors */}
      <div>
        {
          card.colorVariants?.data?.length > 0 && (
            <ProductOptions
              page='category'
              type='color'
              selectedProductId={card.id}
              option={card.colorVariants}
              onHover={handleHover}
              onHoverOut={handleHoverOut}
            />
          )
        }
      </div>
      <div className={cssStyles["text"]}>
        <Typography sx={{ margin: '0px', fontWeight: 700 }} textAlign={"center"} mt={1} mb={2} noWrap={false} gutterBottom variant="h6" component="div">
          {card.name}
          {(Array.isArray(card.otherSizes) && card.otherSizes.length > 0 && Array.isArray(card.otherColors) && card.otherColors.length > 0) ? ` (${card.otherSizes.length + 1} sizes, ${card.otherColors.length + 1} colors)` : ''}
          {(Array.isArray(card.otherSizes) && card.otherSizes.length > 0 && !Array.isArray(card.otherColors)) ? ` (${card.otherSizes.length + 1} sizes)` : ''}
          {(!Array.isArray(card.otherSizes) && Array.isArray(card.otherColors) && card.otherColors.length > 0) ? ` (${card.otherColors.length + 1} colors)` : ''}
        </Typography>
        {(card.price !== undefined && card.price > 0 && card.comparePrice !== undefined && card.comparePrice > 0 && card.price < card.comparePrice) ? (
          <Typography sx={{ margin: '0px', fontWeight: 700 }} textAlign={"center"} variant="subtitle2">
            Price : $<Box component="span" sx={{ textDecoration: "line-through", textDecorationThickness: "2px", textDecorationColor: 'primary.main', marginRight: "10px" }}>{card.comparePrice}</Box>
            <Box component="span" sx={{ color: "primary_extent.mainDark" }}>${card.price}</Box>
            <Box component="span" className={cssStyles["sale"]}>Sale</Box>
          </Typography>
        ) : card.price !== undefined && card.price > 0 && (
          <Typography sx={{ margin: '0px', fontWeight: 700 }} textAlign={"center"} variant="subtitle2" >
            Price : $<span>{card.price}</span>
          </Typography>
        )}
        {card.expiresOn && <Typography sx={{ margin: '0px', color: 'primary.main', fontWeight: 700 }} textAlign={"center"} mt={1} mb={2} noWrap={false} gutterBottom variant="h6" component="div">{card.expiresOn}</Typography>}
        <Typography sx={{ margin: '0px', fontWeight: 700, color: 'primary_extent.mainDark' }} textAlign={"center"} mt={1} mb={2} noWrap={false} gutterBottom variant="h6" component="div">FREE SHIPPING</Typography>
        {card.viewInYourRoomLink && card.model3dUrl && (<>
          <div className={cssStyles["bottom-icon-dsk"]}>
            <a id={`${pageName}_view_in_your_room_icon_${card.id}`} href={card.viewInYourRoomLink} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <div className={cssStyles["button-3d"]}><ViewInArIcon /></div>
              <Typography sx={{ margin: '0px', fontWeight: 700, color: "primary_extent.mainDark", display: 'flex', alignItems: 'center' }} textAlign={"left"} noWrap={false} gutterBottom variant="h6" component="div">View this product in your room</Typography>
            </a>
          </div>
          <div className={cssStyles["view-in-room-mobile"]}>
            <AppleDownloadPopupLayerButton openedFrom={pageName}
              buttonClass={{
                padding: '0px', margin: '0px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '25px', width: '25px', minWidth: '25px', backgroundColor: 'white', borderRadius: '50%'
              }}>
              <div style={{ display: 'flex', flexDirection: 'row', flexFlow: 'row', alignItems: "center", justifyContent: "center" }}>
                {card.model3dUrl &&
                  <div className={cssStyles["button-3d"]}>
                    <ViewInArIcon />
                  </div>
                }
                <Typography sx={{ margin: '0px', fontWeight: 700, color: "primary_extent.mainDark" }} textAlign={"center"} noWrap={false} gutterBottom variant="h6" component="div">
                  View this product in your room
                </Typography>
              </div>
            </AppleDownloadPopupLayerButton>
          </div>
        </>)}
        {card.viewMoreLink && <a href={card.viewMoreLink} className="see-more-like-this">See more like this</a>}
      </div>
    </Box>
  );
};

export default Product;
